// New Run, empty-run picker: a grower can farm more than one fruit (carrots and
// peaches on one account), and a flat variety list lets the operator start a
// peach run meant to be carrots. Group the grower's varieties by the fruit of
// the block that carries them, so the fruit is named on screen BEFORE the pick.
import { compareNatural } from '@/lib/naturalSort'

export interface VarietyGroup { fruit: string | null; varieties: string[] }

/** "PEACHES" → "Peaches", "SOFT CITRUS" → "Soft citrus". Null / blank → null. */
export function fruitLabel(c: string | null | undefined): string | null {
  const t = (c ?? '').trim().replace(/\s+/g, ' ')
  if (!t) return null
  const lower = t.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/**
 * Dedupe varieties (trimmed). A variety seen under two fruits goes under the
 * first NON-null one. Groups sort by fruit name, the no-fruit group ("Other")
 * last; varieties inside a group sort naturally.
 */
export function groupVarietiesByFruit(
  blocks: { variety: string | null; commodity: string | null }[],
): VarietyGroup[] {
  const fruitOf = new Map<string, string | null>()
  for (const b of blocks) {
    const v = (b.variety ?? '').trim()
    if (!v) continue
    const f = (b.commodity ?? '').trim() || null
    if (!fruitOf.has(v) || (fruitOf.get(v) === null && f !== null)) fruitOf.set(v, f)
  }
  const byFruit = new Map<string | null, string[]>()
  for (const [v, f] of fruitOf) byFruit.set(f, [...(byFruit.get(f) ?? []), v])
  return Array.from(byFruit, ([fruit, varieties]) => ({ fruit, varieties: varieties.sort(compareNatural) }))
    .sort((a, b) => {
      if (a.fruit === null) return 1
      if (b.fruit === null) return -1
      return compareNatural(a.fruit, b.fruit)
    })
}

/**
 * New Run: should the create bar warn that the run's fruit has nothing packable?
 * Only when every input is KNOWN: both reads succeeded (a pending or failed read
 * looks like an empty list, and must never read as "no rate cards"), the fruit
 * has at least one active pack format, none of those codes has a rate card, and
 * the flow weighs pallets (a berry lane packs fine without a card).
 */
export function noPackableCode(input: {
  commodity: string | null
  formatsLoaded: boolean
  rulesLoaded: boolean
  formatCodes: readonly string[]
  codesWithRateCard: ReadonlySet<string>
  weighs: boolean
}): boolean {
  if (!input.commodity || !input.formatsLoaded || !input.rulesLoaded || !input.weighs) return false
  if (input.formatCodes.length === 0) return false
  return input.formatCodes.every(c => !input.codesWithRateCard.has(c))
}
